"use client"

import * as React from "react"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  type CarouselApi,
} from "../../components/carousel"
import { cn } from "../../lib/utils"
import { cva, type VariantProps } from "class-variance-authority"
import { UITypography } from "../display/ui-typography"
import { UIBox } from "./ui-box"

// ============================================================================
// UICarousel Types
// ============================================================================

export interface UICarouselProps extends VariantProps<typeof carouselVariants> {
  /**
   * 캐러셀 아이템들
   */
  items: React.ReactNode[]
  /**
   * 자동 재생 여부
   */
  autoplay?: boolean
  /**
   * 자동 재생 간격 (밀리초)
   */
  interval?: number
  /**
   * 도트 네비게이션 표시 여부
   */
  showDots?: boolean
  /**
   * 화살표 버튼 표시 여부
   */
  showArrows?: boolean
  /**
   * 무한 루프 여부
   */
  loop?: boolean
  /**
   * 한 번에 보여줄 슬라이드 개수
   */
  slidesToShow?: number
  /**
   * 컨테이너 클래스명
   */
  className?: string
  /**
   * 캐러셀 API 참조
   */
  onApiChange?: (api: CarouselApi) => void
  /**
   * 슬라이드 변경 콜백
   */
  onSlideChange?: (index: number) => void
}

const carouselVariants = cva(
  "w-full max-w-full min-w-0 flex-shrink-0 relative", // Width Stability 패턴
  {
    variants: {
      size: {
        sm: "max-w-sm",
        default: "max-w-4xl",
        lg: "max-w-6xl",
        full: "max-w-full",
      },
      variant: {
        default: "bg-background",
        contained: "bg-background rounded-lg border shadow-sm p-4",
        elevated: "bg-background rounded-xl border shadow-lg p-6",
        minimal: "bg-transparent",
      },
      spacing: {
        none: "",
        sm: "gap-2",
        default: "gap-4",
        lg: "gap-6",
      },
    },
    defaultVariants: {
      size: "default",
      variant: "default",
      spacing: "default",
    },
  }
)

// ============================================================================
// UICarousel Component
// ============================================================================

export const UICarousel = React.forwardRef<
  HTMLDivElement,
  UICarouselProps
>(({
  items,
  autoplay = false,
  interval = 3000,
  showDots = true,
  showArrows = true,
  loop = true,
  slidesToShow = 1,
  size,
  variant,
  spacing,
  className,
  onApiChange,
  onSlideChange,
  ...props
}, ref) => {
  const [api, setApi] = React.useState<CarouselApi>()
  const [current, setCurrent] = React.useState(0)
  const [count, setCount] = React.useState(0)
  const autoplayRef = React.useRef<NodeJS.Timeout | undefined>(undefined)

  // API 초기화 및 상태 설정
  React.useEffect(() => {
    if (!api) return

    setCount(api.scrollSnapList().length)
    setCurrent(api.selectedScrollSnap() + 1)

    api.on("select", () => {
      const index = api.selectedScrollSnap()
      setCurrent(index + 1)
      onSlideChange?.(index)
    })

    onApiChange?.(api)
  }, [api, onApiChange, onSlideChange])

  // 자동 재생 기능
  React.useEffect(() => {
    if (!api || !autoplay) return

    const startAutoplay = () => {
      autoplayRef.current = setInterval(() => {
        if (api.canScrollNext()) {
          api.scrollNext()
        } else if (loop) {
          api.scrollTo(0)
        }
      }, interval)
    }

    const stopAutoplay = () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current)
      }
    }

    startAutoplay()

    // 사용자 상호작용 시 자동 재생 일시 정지
    const carousel = api.containerNode()
    const handleMouseEnter = stopAutoplay
    const handleMouseLeave = startAutoplay

    carousel.addEventListener('mouseenter', handleMouseEnter)
    carousel.addEventListener('mouseleave', handleMouseLeave)

    return () => {
      stopAutoplay()
      carousel.removeEventListener('mouseenter', handleMouseEnter)
      carousel.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [api, autoplay, interval, loop])

  // 도트 클릭 핸들러
  const handleDotClick = (index: number) => {
    api?.scrollTo(index)
  }

  if (items.length === 0) {
    return null
  }

  return (
    <UIBox
      ref={ref}
      className={cn(carouselVariants({ size, variant, spacing }), className)}
      {...props}
    >
      <Carousel
        setApi={setApi}
        opts={{
          loop,
          align: "start",
        }}
        className="group w-full"
      >
        <CarouselContent className="ml-1">
          {items.map((item, index) => (
            <CarouselItem
              key={index}
              className={cn(
                "pl-1",
                // 반응형 basis 클래스 개선
                slidesToShow === 1 && "basis-full",
                slidesToShow === 2 && "basis-full sm:basis-1/2",
                slidesToShow === 3 && "basis-full sm:basis-1/2 md:basis-1/3",
                slidesToShow === 4 && "basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/4",
                slidesToShow > 4 && "basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/4 xl:basis-1/5"
              )}
            >
              <UIBox className="p-1">
                {item}
              </UIBox>
            </CarouselItem>
          ))}
        </CarouselContent>

        {/* 향상된 화살표 버튼 */}
        {showArrows && items.length > 1 && (
          <>
            <CarouselPrevious
              className={cn(
                "left-0 top-1/2 -translate-y-1/2",
                "h-8 w-8 rounded-full border border-border/40",
                "bg-background/80",
                "shadow-lg hover:shadow-xl",
                "transition-[opacity,background-color,border-color,box-shadow,transform,translate,scale] duration-200",
                "hover:bg-background hover:border-border",
                "opacity-0 group-hover:opacity-100",
                "hover:scale-110"
              )}
              size="icon"
              variant="ghost"
            />
            <CarouselNext
              className={cn(
                "right-0 top-1/2 -translate-y-1/2",
                "h-8 w-8 rounded-full border border-border/40",
                "bg-background/80",
                "shadow-lg hover:shadow-xl",
                "transition-[opacity,background-color,border-color,box-shadow,transform,translate,scale] duration-200",
                "hover:bg-background hover:border-border",
                "opacity-0 group-hover:opacity-100",
                "hover:scale-110"
              )}
              size="icon"
              variant="ghost"
            />
          </>
        )}
      </Carousel>

      {/* 향상된 도트 네비게이션 */}
      {showDots && items.length > 1 && (
        <UIBox className="flex justify-center items-center mt-6 space-x-2">
          {Array.from({ length: count }).map((_, index) => (
            <button
              key={index}
              type="button"
              aria-label={`슬라이드 ${index + 1}로 이동`}
              className={cn(
                "relative h-2 rounded-full transition-[width,background-color,box-shadow,transform,translate,scale] duration-300 ease-in-out",
                "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                "hover:scale-125",
                current === index + 1
                  ? "w-8 bg-primary shadow-sm"
                  : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/60"
              )}
              onClick={() => handleDotClick(index)}
            >
              {/* 진행률 표시를 위한 내부 엘리먼트 */}
              {current === index + 1 && (
                <UIBox className="absolute inset-0 rounded-full bg-primary-foreground/20" />
              )}
            </button>
          ))}
        </UIBox>
      )}
    </UIBox>
  )
})

UICarousel.displayName = "UICarousel"

// ============================================================================
// Preset Components
// ============================================================================

export interface UICarouselImageProps extends Omit<UICarouselProps, 'items'> {
  images: {
    src: string
    alt: string
    title?: string
  }[]
  aspectRatio?: 'square' | 'video' | 'wide' | 'tall'
}

export const UICarouselImage = React.forwardRef<
  HTMLDivElement,
  UICarouselImageProps
>(({
  images,
  aspectRatio = 'video',
  ...props
}, ref) => {
  const items = images.map((image, index) => (
    <UIBox
      key={index}
      className={cn(
        "group relative overflow-hidden rounded-xl bg-muted border shadow-sm",
        "hover:shadow-md transition-shadow duration-300",
        aspectRatio === 'square' && "aspect-square",
        aspectRatio === 'video' && "aspect-video",
        aspectRatio === 'wide' && "aspect-[21/9]",
        aspectRatio === 'tall' && "aspect-[3/4]"
      )}
    >
      <img
        src={image.src}
        alt={image.alt}
        title={image.title}
        className={cn(
          "object-cover w-full h-full transition-transform duration-300",
          "group-hover:scale-105"
        )}
        loading="lazy"
      />
      {/* 이미지 위 그라디언트 오버레이 */}
      <UIBox className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      {/* 이미지 제목 표시 */}
      {image.title && (
        <UIBox className="absolute bottom-0 left-0 right-0 p-4 text-white bg-gradient-to-t from-black/60 to-transparent">
          <UITypography variant="h3" size="sm" weight="medium" className="truncate text-white">{image.title}</UITypography>
        </UIBox>
      )}
    </UIBox>
  ))

  return <UICarousel ref={ref} items={items} variant="minimal" {...props} />
})

UICarouselImage.displayName = "UICarouselImage"

export interface UICarouselTestimonialProps extends Omit<UICarouselProps, 'items'> {
  testimonials: {
    content: string
    author: string
    role?: string
    avatar?: string
    rating?: number
  }[]
}

export const UICarouselTestimonial = React.forwardRef<
  HTMLDivElement,
  UICarouselTestimonialProps
>(({
  testimonials,
  ...props
}, ref) => {
  const items = testimonials.map((testimonial, index) => (
    <UIBox
      key={index}
      className={cn(
        "relative p-8 text-center",
        "bg-card rounded-xl border shadow-sm",
        "hover:shadow-md transition-[box-shadow,transform,translate,scale] duration-300",
        "hover:scale-[1.02]"
      )}
    >
      {/* 인용 아이콘 */}
      <UIBox className="absolute top-4 left-4 text-muted-foreground/20">
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z"/>
        </svg>
      </UIBox>

      {/* 별점 */}
      {testimonial.rating && (
        <UIBox className="flex justify-center mb-6">
          {Array.from({ length: 5 }).map((_, i) => (
            <svg
              key={i}
              className={cn(
                "w-5 h-5 transition-colors",
                i < testimonial.rating!
                  ? "text-yellow-400 fill-current"
                  : "text-muted-foreground/30"
              )}
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          ))}
        </UIBox>
      )}

      {/* 인용문 */}
      <UITypography variant="blockquote" weight="medium" className="mb-6 leading-relaxed">
        "{testimonial.content}"
      </UITypography>

      {/* 작성자 정보 */}
      <UIBox className="flex items-center justify-center space-x-4">
        {testimonial.avatar && (
          <UIBox className="relative">
            <img
              src={testimonial.avatar}
              alt={testimonial.author}
              className="w-12 h-12 rounded-full object-cover border-2 border-border shadow-sm"
            />
            <UIBox className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-background" />
          </UIBox>
        )}
        <UIBox className="text-left">
          <UITypography variant="p" size="sm" weight="semibold">{testimonial.author}</UITypography>
          {testimonial.role && (
            <UITypography variant="p" size="xs" color="muted">{testimonial.role}</UITypography>
          )}
        </UIBox>
      </UIBox>
    </UIBox>
  ))

  return <UICarousel ref={ref} items={items} variant="minimal" {...props} />
})

UICarouselTestimonial.displayName = "UICarouselTestimonial"

// ============================================================================
// Type Re-exports
// ============================================================================
// Note: All types are automatically exported via their interface declarations